import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';
import TicketRichTextEditor from '../rich-text/TicketRichTextEditor';

/** Merge tags available to insert into the template body/subject. */
const MERGE_VARIABLES = [
  { token: '{{first_name}}', label: 'First name' },
  { token: '{{last_name}}', label: 'Last name' },
  { token: '{{company_name}}', label: 'Company name' },
];

/**
 * Email template editor. Persists name, subject, and raw HTML body to
 * `companies/{companyId}/crm_templates` (tagged with the active app) using
 * an auto-generated id.
 * @param {{ userData?: { companyId?: string }, activeAppId?: string, activeAppName?: string }} props
 */
export default function TemplateEditor({ userData, activeAppId = '', activeAppName = '' }) {
  const { companyId } = useWorkspace();
  const resolvedCompanyId = companyId || userData?.companyId || null;

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const insertVariable = (token) => {
    setBody((prev) => {
      const current = (prev || '').trim();
      // Append the exact merge tag; TipTap re-wraps loose text into a paragraph.
      return current ? `${prev} ${token}` : `<p>${token}</p>`;
    });
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!resolvedCompanyId) {
      setError('No workspace is linked to your account.');
      return;
    }
    if (!activeAppId) {
      setError('Select an app context before saving a template.');
      return;
    }
    if (!name.trim()) {
      setError('Enter a template name.');
      return;
    }
    if (!subject.trim()) {
      setError('Enter an email subject line.');
      return;
    }
    setSaving(true);
    try {
      const ref = await addDoc(
        collection(db, 'companies', resolvedCompanyId, 'crm_templates'),
        {
          appId: activeAppId,
          appName: activeAppName || null,
          name: name.trim(),
          subject: subject.trim(),
          bodyHtml: body || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdByUid: auth.currentUser?.uid || null,
        },
      );
      setSuccess(
        `Template "${name.trim()}" saved for ${activeAppName || 'app'} (ID: ${ref.id}).`,
      );
      setName('');
      setSubject('');
      setBody('');
    } catch (e) {
      console.error('save crm template:', e);
      setError(e.message || 'Could not save template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Email template editor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Compose a reusable email for <strong>{activeAppName || '—'}</strong>. Use merge tags to
        personalize each send.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Stack spacing={2}>
        <TextField
          label="Template name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          placeholder="e.g. Welcome email"
          helperText="Internal name — recipients never see this."
        />

        <TextField
          label="Email subject line"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          fullWidth
          placeholder="e.g. Welcome to {{company_name}}!"
        />

        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1 }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
              <DataObjectIcon fontSize="small" />
              <Typography variant="caption">Insert variable:</Typography>
            </Stack>
            {MERGE_VARIABLES.map((v) => (
              <Chip
                key={v.token}
                label={v.token}
                size="small"
                variant="outlined"
                onClick={() => insertVariable(v.token)}
                clickable
                title={`Insert ${v.label}`}
              />
            ))}
          </Stack>

          <TicketRichTextEditor
            value={body}
            onChange={setBody}
            minHeight={220}
            placeholder="Write your email content here…"
          />
        </Box>

        <Box>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            Save Template
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
